/* Fills the tokenized REPORT_TEMPLATE with a ReportPayload; same string renders both the iframe and the download. */
import { getReportTemplate } from "../constants/reportTemplate";
import { complianceClass } from "./reportHelpers";
import { cleanEvidenceHtml } from "./mapReportData";
import type {
  ReportPayload,
  ReportMeta,
  CatalogSection,
  ExecutiveSection,
  ControlImplSection,
  ImplControl,
  AssessmentSection,
  FindingsSection,
  HistorySection,
  TrendSection,
  FindingsSnapshot,
} from "../types/reportTypes";

/* ── helpers ──────────────────────────────────────────────────────────── */
const esc = (s: unknown): string =>
  String(s == null ? "" : s).replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!,
  );

const fmtDate = (iso: unknown): string => {
  if (!iso) return "—";
  const s = String(iso).trim();
  if (!s || s === "—") return "—";
  const p = (n: number) => (n < 10 ? "0" + n : "" + n);
  const ymdMatch = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T\s].*)?$/);
  if (ymdMatch) {
    return `${p(Number(ymdMatch[3]))}/${p(Number(ymdMatch[2]))}/${ymdMatch[1]}`;
  }
  const dmyMatch = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (dmyMatch) {
    return `${p(Number(dmyMatch[1]))}/${p(Number(dmyMatch[2]))}/${dmyMatch[3]}`;
  }
  if (/^\d{10,13}$/.test(s)) {
    const num = Number(s);
    const d = new Date(s.length === 10 ? num * 1000 : num);
    if (!isNaN(d.getTime())) {
      return `${p(d.getUTCDate())}/${p(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`;
    }
  }
  const d = new Date(s + (/\d{2}:\d{2}/.test(s) ? "" : "T00:00:00"));
  if (isNaN(d.getTime())) return s;
  return `${p(d.getUTCDate())}/${p(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`;
};

const eye =
  '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>';

/* ── metadata ─────────────────────────────────────────────────────────── */
function metadata(m: ReportMeta): string {
  const plans = m.assessmentPlans.map((p) => `<span>${esc(p)}</span>`).join('<span>, </span>') || `<span>—</span>`;
  return `<div class="meta meta-top">
        <div class="meta-item"><label>System Security Plan Name</label><span>${esc(m.sspName)}</span></div>
        <div class="meta-item"><label>Assessment Plan${m.assessmentPlans.length > 1 ? `s (${m.assessmentPlans.length})` : ""}</label><span>${plans}</span></div>
    </div>
    <div class="meta meta-dates">
        <div class="meta-item"><label>Assessment Start Date</label><span>${fmtDate(m.assessmentStartDate)}</span></div>
        <div class="meta-item"><label>Assessment End Date</label><span>${fmtDate(m.assessmentEndDate)}</span></div>
        <div class="meta-item"><label>Report Generated On</label><span>${fmtDate(m.generatedOn)}</span></div>
        <div class="meta-item"><label>Report Generated By</label><span>${esc(m.generatedBy || "—")}</span></div>
    </div>`;
}

/* ── catalog cards (two name/level pairs) ─────────────────────────────── */
const cleanCardVersion = (v?: string) =>
  esc((v || "1.0.0").replace(/^v/i, "").replace(/[()]/g, "").trim() || "1.0.0");

const catalogCard = (c: CatalogSection) =>
  `<div class="catalog-name">${esc(c.catalog.title || "Catalog")}</div>\n<div class="catalog-level">${cleanCardVersion(c.catalog.version)}${c.catalog.status ? ` — ${esc(c.catalog.status)}` : ""}</div>`;
const profileCard = (c: CatalogSection) =>
  `<div class="catalog-name">${esc(c.profile.title || "Profile")}</div>\n<div class="catalog-level">${cleanCardVersion(c.profile.version)}${c.profile.status ? ` — ${esc(c.profile.status)}` : ""}</div>`;

/* ── executive summary ────────────────────────────────────────────────── */
function execKpi(e: ExecutiveSection): string {
  const k = e.kpis;
  const rate = `exec-kpi-${complianceClass(k.compliancePercent)}`;
  return `<div class="exec-kpi-strip">
            <div class="exec-kpi-box exec-kpi-neutral"><span class="exec-kpi-label">Assessed Controls</span><span class="exec-kpi-value">${k.assessed} / ${k.baselineTotal}</span><span class="exec-kpi-sub">Baseline Profile Controls Assessed</span></div>
            <div class="exec-kpi-box ${rate}"><span class="exec-kpi-label">Compliance Rate</span><span class="exec-kpi-value"><span>${k.compliancePercent}</span><span class="exec-kpi-unit">%</span></span><span class="exec-kpi-sub">${k.satisfied} of ${k.assessed} controls satisfied</span></div>
            <div class="exec-kpi-box exec-kpi-green"><span class="exec-kpi-label">Satisfied Controls</span><span class="exec-kpi-value">${k.satisfied}</span><span class="exec-kpi-sub">of ${k.assessed} assessed</span></div>
            <div class="exec-kpi-box exec-kpi-red"><span class="exec-kpi-label">Not Satisfied Controls</span><span class="exec-kpi-value">${k.notSatisfied}</span><span class="exec-kpi-sub">of ${k.assessed} assessed</span></div>
        </div>`;
}
const ciaCls = (l: string) => (l === "high" ? "cia-high" : l === "moderate" ? "cia-moderate" : l === "low" ? "cia-low" : "cia-unknown");
function execCia(e: ExecutiveSection): string {
  const c = e.cia;
  return `<div class="cia-strip">
            <div class="cia-chip"><label>Confidentiality</label><span class="${ciaCls(c.confidentiality)}">${esc(c.confidentiality)}</span></div>
            <div class="cia-chip"><label>Integrity</label><span class="${ciaCls(c.integrity)}">${esc(c.integrity)}</span></div>
            <div class="cia-chip"><label>Availability</label><span class="${ciaCls(c.availability)}">${esc(c.availability)}</span></div>
            <div class="cia-chip cia-chip-overall"><label>Overall Impact</label><span class="${ciaCls(c.overall)}">${esc(c.overall)}</span></div>
        </div>`;
}
/* Executive summary is AI-generated from a slow endpoint; #execSummaryContent is filled in-place once it arrives. */
export type ExecSummaryState = "loading" | "ready" | "error";

const EXEC_STYLE =
  `<style>.exec-summary-loading{display:flex;align-items:center;gap:10px;padding:14px 0;color:#64748b;font-size:13px;}` +
  `.exec-spinner{width:16px;height:16px;border:2px solid #e2e8f0;border-top-color:#4f46e5;border-radius:50%;animation:execspin .7s linear infinite;flex:0 0 auto;}` +
  `@keyframes execspin{to{transform:rotate(360deg);}}` +
  `.exec-summary-error{padding:12px 14px;background:#fff5f5;border:1px solid #fed7d7;border-radius:8px;color:#9b2c2c;font-size:13px;}</style>`;

/* Splits AI summary text on blank lines; only <b>/<strong>/<br> pass through, everything else is escaped. */
export function execSummaryParagraphsHtml(text: string): string {
  const sanitize = (p: string) =>
    p.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/&lt;(\/?)(b|strong)&gt;/g, "<$1$2>")
      .replace(/&lt;br\s*\/?&gt;/g, "<br>");
  return (text || "")
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p class="summary-text">${sanitize(p)}</p>`)
    .join("\n        ");
}

export const execSummaryLoadingHtml = (): string =>
  `<div class="exec-summary-loading"><span class="exec-spinner" aria-hidden="true"></span><span>Generating executive summary... (this can take up to 10 seconds)</span></div>`;

export const execSummaryErrorHtml = (): string =>
  `<div class="exec-summary-error">Could not generate executive summary. The rest of the report is still complete.</div>`;

/* Inner HTML for #execSummaryContent for a given state. */
export function execSummaryInner(state: ExecSummaryState, text?: string): string {
  if (state === "loading") return execSummaryLoadingHtml();
  if (state === "error") return execSummaryErrorHtml();
  return execSummaryParagraphsHtml(text || "");
}

function execNarrative(
  e: ExecutiveSection,
  exec: { state: ExecSummaryState; text?: string },
): string {
  const callout = e.kpis.notSatisfied > 0
    ? `\n        <p class="needs-attention-callout"><a href="javascript:void(0)" class="needs-attention-link" role="button" onclick="(function(){var el=document.getElementById('carSection');if(el){el.scrollIntoView({behavior:'smooth',block:'start'});}})();return false;">Check assessed controls here <span class="needs-attention-arrow" aria-hidden="true">→</span></a></p>`
    : "";
  return `${EXEC_STYLE}<div id="execSummaryContent">${execSummaryInner(exec.state, exec.text)}</div>${callout}`;
}

/* ── control implementation table rows (drives Chart/Table/Network) ───── */
const statusBadgeCls = (s: ImplControl["status"]) => {
  const norm = String(s ?? "").toLowerCase().replace(/_/g, "-").trim();
  if (norm === "implemented") return "badge-implemented";
  if (norm === "partial" || norm === "partially-implemented") return "badge-partial";
  if (norm === "planned") return "badge-planned";
  if (norm === "alternative") return "badge-alternative";
  if (norm === "not-applicable") return "badge-not-applicable";
  return "badge-not-implemented";
};

const auditBadgeCls = (s?: string) => {
  const norm = String(s ?? "").toLowerCase().replace(/_/g, "-").trim();
  if (norm === "satisfied") return "badge-satisfied";
  if (norm === "not-satisfied" || norm === "not satisfied") return "badge-not-satisfied";
  return "badge-pending";
};

function ciControlRow(group: string, c: ImplControl): string {
  const comps = c.components.length
    ? `<div>${c.components.map((x) => `<div class="comp-tag"><span class="comp-name">${esc(x.name)}</span><span class="comp-type">${esc(x.type)}</span></div>`).join("")}</div>`
    : `<span class="empty-cell">No component</span>`;
  const inv = c.inventory.length
    ? `<div>${c.inventory.map((it) => `<div class="inv-item" data-component="${esc(it.component ?? "")}"><div class="inv-desc">Device: ${esc(it.device)}</div><div class="inv-assets">${it.assets.map((a) => `<span class="inv-asset-tag"><span class="inv-asset-type">${esc(a.type)}</span><span class="inv-asset-value">${esc(a.value)}</span></span>`).join("")}</div></div>`).join("")}</div>`
    : `<span class="empty-cell">No inventory</span>`;

  const status = c.status || c.implementationStatus || "not-implemented";

  return `<tr class="group-child hidden-row ci-data-row" data-parent-group="${esc(group)}" data-status="${esc(status)}" data-has-comp="${c.components.length > 0}" data-has-inv="${c.inventory.length > 0}">
                            <td class="num">1</td>
                            <td><span class="ctrl-id">${esc(c.controlId)}</span></td>
                            <td>${esc(c.title)}</td>
                            <td style="min-width: 160px;">${comps}</td>
                            <td style="min-width: 200px;">${inv}</td>
                            <td class="status-cell"><span class="badge ${statusBadgeCls(status)}">${esc(status)}</span></td>
                        </tr>`;
}
function ciRows(ci: ControlImplSection, _assessedControlIds?: Set<string>): string {
  return ci.groups.map((g) => {
    const total = g.sspBaselineTotal ?? g.controls.length;
    const s = g.summary;
    const assessed = s?.assessedControlCount ?? g.controls.length;
    const isImpl = (c: ImplControl) => {
      const st = String(c.status || c.implementationStatus || "").toLowerCase().replace(/_/g, "-").trim();
      return st === "implemented";
    };
    const isPartImpl = (c: ImplControl) => {
      const st = String(c.status || c.implementationStatus || "").toLowerCase().replace(/_/g, "-").trim();
      return st === "partial" || st === "partially-implemented";
    };
    const impl = s?.implementedControlCount ?? g.controls.filter(isImpl).length;
    const partImpl = g.controls.filter(isPartImpl).length;
    const notImpl = Math.max(0, total - impl - partImpl);
    const uniqueComps = new Set<string>();
    const uniqueInv = new Set<string>();
    g.controls.forEach((c) => {
      c.components.forEach((x) => x.name && uniqueComps.add(x.name));
      c.inventory.forEach((iv) => iv.device && uniqueInv.add(iv.device));
    });

    const badgesHtml = [
      `<span class="group-badge gb-total">${total} Controls</span>`,
      `<span class="group-badge gb-impl">${impl + partImpl} Implemented</span>`,
      partImpl > 0 ? `<span class="group-badge gb-part-impl">${partImpl} Partially Implemented</span>` : "",
      `<span class="group-badge gb-not-impl">${notImpl} Not Implemented</span>`,
      `<span class="group-badge gb-comp">${uniqueComps.size} Component</span>`,
      `<span class="group-badge gb-inv">${uniqueInv.size} Inventory</span>`,
    ].filter(Boolean).join("");

    const head = `<tr class="group-row" data-group-title="${esc(g.title)}" onclick="toggleGroup(this)" data-group="${esc(g.key)}" data-baseline-total="${total}" data-implemented-count="${impl}" data-partially-implemented-count="${partImpl}" data-assessed-count="${assessed}" data-not-implemented-count="${notImpl}">
                            <td colspan="6"><span class="group-label"><span class="group-toggle">&#9654;</span><span class="group-id">${esc(g.key)}</span><span class="group-title">${esc(g.title)}</span></span><span class="group-badges">${badgesHtml}</span></td>
                        </tr>`;
    const colHeader = `<tr class="group-child group-col-header hidden-row" data-parent-group="${esc(g.key)}">
                            <th scope="col" class="num group-col-th">#</th><th scope="col" class="group-col-th">Control ID</th><th scope="col" class="group-col-th">Control Title</th><th scope="col" class="group-col-th">Components</th><th scope="col" class="group-col-th">Inventory</th><th scope="col" class="center group-col-th">Status</th>
                        </tr>`;
    const rows = g.controls.map((c) => ciControlRow(g.key, c)).join("");
    return head + colHeader + rows;
  }).join("");
}

/* ── control assessment results rows (drives the Details + per-row asset modals) ── */
function carRows(a: AssessmentSection, sspName: string, catalog: CatalogSection): string {
  return a.results.map((r, i) => {
    const detCls = r.determination === "satisfied" ? "badge-satisfied" : r.determination === "not-satisfied" ? "badge-not-satisfied" : "badge-pending";
    const detText = r.determination.replace(/-/g, " ");
    const search = esc(`${r.controlId} ${r.controlTitle} ${r.statementId} ${r.statement}`);
    const failed = r.failedAssetCount ?? 0;
    const passed = r.passedAssetCount ?? 0;
    const total = failed + passed;

    // Evidence cell: prefer the backend's pre-rendered HTML; else synth from refs.
    // Automation (OPA) rows carry no dataSourceHtml — their evidence is the per-asset
    // pass/fail breakdown, so render that (clickable, wired by wireAssetButtons()) instead
    // of "N/A" whenever asset counts are present.
    const hasDs = r.dataSourceHtml && r.dataSourceHtml !== "No evidence";
    const assetChip = (kind: "passed" | "failed", count: number, json?: string) => {
      const cls = kind === "failed" ? "asset-count-failed" : "asset-count-passed";
      const label = kind === "passed" ? " Passed" : " Failed";
      if (!json) return `<span class="${cls}">${count}${label}</span>`;
      const attr = kind === "failed" ? "data-failed-assets" : "data-passed-assets";
      return `<a href="#" class="${cls} asset-count-link asset-modal-btn" data-control-id="${esc(r.controlId)}" data-control-title="${esc(r.controlTitle)}" data-asset-kind="${kind}" ${attr}="${esc(json)}" onclick="return false;">${count}${label}</a>`;
    };
    const assetSummary = total > 0
      ? [passed > 0 ? assetChip("passed", passed, r.passedAssetsJson) : null, failed > 0 ? assetChip("failed", failed, r.failedAssetsJson) : null]
          .filter(Boolean)
          .join(", ")
      : "";
    // Automated-evidence "Preview" — opens the full scan-evidence sheet (§ Control / §
    // Scan Summary / § Per-Asset Rule Evaluation). Only for OPA rows with no attested
    // file and at least one scanned asset; replaces the asset-count chips below (the
    // sheet itself surfaces the passed/failed breakdown, so the chip is redundant here).
    const isAutomated = String(r.evidenceType ?? "").toLowerCase() === "automation";
    const carPreviewBtn = isAutomated && !hasDs && total > 0
      ? `<a href="#" class="evidence-link car-preview-btn" data-control-id="${esc(r.controlId)}" data-control-title="${esc(r.controlTitle)}" data-statement-text="${esc(r.statement)}" data-audit-status="${esc(r.determination)}" data-failed-count="${failed}" data-passed-count="${passed}" data-failed-assets="${esc(r.failedAssetsJson ?? "[]")}" data-passed-assets="${esc(r.passedAssetsJson ?? "[]")}" data-ssp="${esc(sspName)}" data-profile="${esc(catalog.profile.title)}" data-catalog="${esc(catalog.catalog.title)}" onclick="return false;">Preview</a>`
      : "";
    const evidenceCell = hasDs
      ? `<span>${cleanEvidenceHtml(r.dataSourceHtml)}</span>`
      : carPreviewBtn
        ? `<span>${carPreviewBtn}</span>`
        : assetSummary
          ? `<span>${assetSummary}</span>`
          : r.evidence.length
            ? `<span><a href="javascript:void(0)" class="evidence-link" onclick="showEvidence({title:'${esc(r.evidence[0].title)}',fileName:'No file',statementId:'${esc(r.statementId)}',hasFile:false})">Preview</a></span>`
            : `<span class="empty-cell">N/A</span>`;

    const assetAttrs = total > 0
      ? ` data-failed-count="${failed}" data-passed-count="${passed}" data-failed-assets="${esc(r.failedAssetsJson ?? "[]")}" data-passed-assets="${esc(r.passedAssetsJson ?? "[]")}"`
      : "";

    return `<tr data-status="${esc(r.determination)}" data-type="${esc(r.evidenceType ?? "")}" data-control-id="${esc(r.controlId)}" data-search="${search}" data-total-assets="${total}" data-asset-passed-count="${passed}" data-asset-failed-count="${failed}">
                    <td class="num">${i + 1}</td>
                    <td><span class="adp-id">${esc(r.controlId)}</span></td>
                    <td><span>${esc(r.controlTitle)}</span></td>
                    <td><span>${esc(r.statement)}</span></td>
                    <td class="center type-cell"><span class="type-badge">${esc(r.evidenceType ?? "—")}</span></td>
                    <td class="status-cell"><span class="badge ${detCls}">${esc(detText)}</span></td>
                    <td class="center evidence-cell">${evidenceCell}</td>
                    <td class="center"><button type="button" class="icon-btn car-details-btn" aria-label="View control details" title="View details" data-control-id="${esc(r.controlId)}" data-control-title="${esc(r.controlTitle)}" data-statement-id="${esc(r.statementId)}" data-statement-text="${esc(r.statement)}" data-audit-status="${esc(r.determination)}" data-evidence-type="${esc(r.evidenceType ?? "Manual")}" data-data-source="${esc(cleanEvidenceHtml(r.dataSourceHtml) || "No evidence")}" data-justification="${esc(r.justification ?? "")}"${assetAttrs}>${eye}</button></td>
                </tr>`;
  }).join("");
}

/* ── findings rows + recent activity ──────────────────────────────────── */
function findingsRows(f: FindingsSection): string {
  return f.rows.map((r, i) => {
    const sevCls = r.severity === "none" ? "badge-severity-none" : `badge-severity-${r.severity}`;
    const sevText = r.severity === "none" ? "No Severity" : r.severity;
    const statusText = r.status.replace(/_/g, " ");
    const search = esc(`${r.controlId} ${r.title} ${r.status} ${r.severity} ${r.controlTitle ?? ""}`);
    return `<tr data-category="remediation" data-severity="${esc(r.severity)}" data-search="${search}">
                    <td class="num">${i + 1}</td>
                    <td><span>${esc(r.title)}</span></td>
                    <td class="text-capitalize">${esc(statusText)}</td>
                    <td class="status-cell"><span class="badge ${sevCls}">${esc(sevText)}</span></td>
                    <td class="center"><button type="button" class="icon-btn findings-details-btn" aria-label="View finding details" title="View details" data-control-id="${esc(r.controlId)}" data-control-title="${esc(r.controlTitle ?? r.title)}" data-poam-title="${esc(r.title)}" data-statement-id="${esc(r.statementId ?? "")}" data-statement-description="${esc(r.statementDescription ?? "")}" data-severity="${esc(r.severity)}" data-status="${esc(r.status)}" data-description="${esc(r.description ?? r.title)}" data-created-at="${esc(r.createdAt ?? "")}" data-updated-at="${esc(r.updatedAt ?? "")}" data-poam-item-uuid="${esc(r.poamItemUuid ?? "")}" data-audit-log="${esc(r.auditLogJson ?? "[]")}">${eye}</button></td>
                </tr>`;
  }).join("");
}
function raItems(f: FindingsSection): string {
  return f.recentActivity.map((a, i) =>
    `<li class="ra-item"><span class="ra-num">${i + 1}</span><span class="ra-date">${fmtDate(a.date)}</span><span class="ra-body"><span class="ra-id">${esc(a.controlId)}</span><span class="ra-finding">${esc(a.title)}</span></span><span class="ra-status"><span class="badge badge-not-satisfied">${esc(a.status)}</span></span></li>`,
  ).join("");
}

/* ── assessment history rows ──────────────────────────────────────────── */
function historyRows(h: HistorySection): string {
  return h.rows.map((r, i) => {
    const cls = complianceClass(r.compliancePercent);
    const primaryStyle = r.primary ? ' style="background:#f0fff4;font-weight:500"' : "";
    const primaryBadge = r.primary ? ' <span class="badge badge-implemented" style="margin-left:6px;font-size:9px;">Primary</span>' : "";
    const pending = r.pendingReview > 0
      ? `<span class="ah-pending-chip" title="${r.pendingReview} control(s) awaiting reviewer sign-off"><span>${r.pendingReview}</span> pending review</span>`
      : "";
    const search = esc(`${r.plan} ${r.status} ${r.startDate} ${r.endDate}`);
    return `<tr class="assessment-history-row"${primaryStyle} data-start="${esc(r.startDate)}" data-end="${esc(r.endDate)}" data-status="${esc(r.status)}" data-search="${search}">
                    <td class="num">${i + 1}</td>
                    <td><span>${esc(r.plan)}</span>${primaryBadge}</td>
                    <td class="status-cell"><span class="badge badge-info" style="text-transform:capitalize">${esc(r.status.replace(/_/g, " "))}</span></td>
                    <td class="period-cell"><span class="period-start">${fmtDate(r.startDate)}</span><span class="period-arrow">→</span><span class="period-end">${fmtDate(r.endDate)}</span></td>
                    <td class="num">${r.inPlan}</td>
                    <td class="num">${r.assessed}</td>
                    <td class="num" style="color:#22543d;font-weight:600">${r.passed}</td>
                    <td class="num" style="color:#9b2c2c;font-weight:600">${r.failed}</td>
                    <td class="compliance-percent-cell"><div class="compliance-bar"><div class="compliance-bar-track"><div class="compliance-bar-fill ${cls}" style="width:${r.compliancePercent}%"></div></div><span class="compliance-bar-value">${r.compliancePercent}%</span></div></td>
                    <td class="center"><div class="ah-trend-stack"><span style="color:#718096;font-size:12px;">&#9644;</span>${pending}</div></td>
                </tr>`;
  }).join("");
}

/* ── data islands (snapshot + activity + trend + evidence) ────────────── */
function dataIslands(
  snap: FindingsSnapshot,
  trend: TrendSection,
  ci: ControlImplSection,
  embeddedEvidence?: EmbeddedEvidence,
  audience: ReportAudience = "system_owner",
): string {
  const severity = [
    { key: "critical", label: "Critical", count: snap.bySeverity.critical, accent: "#c53030", tint: "#fff5f5", icon: "⚠", desc: "Immediate remediation required" },
    { key: "high", label: "High", count: snap.bySeverity.high, accent: "#c05621", tint: "#fff8f3", icon: "▲", desc: "Address within the current cycle" },
    { key: "medium", label: "Medium", count: snap.bySeverity.medium, accent: "#b7791f", tint: "#fffaf0", icon: "◆", desc: "Plan remediation" },
    { key: "low", label: "Low", count: snap.bySeverity.low, accent: "#2b6cb0", tint: "#ebf8ff", icon: "▼", desc: "Monitor or accept" },
    { key: "none", label: "No Severity", count: snap.bySeverity.none, accent: "#64748b", tint: "#f7fafc", icon: "○", desc: "Severity not yet assigned" },
  ];
  const status = [
    { key: "identified", label: "Identified", count: snap.byStatus.identified, accent: "#64748b", tint: "#eef2f7", icon: "◎", desc: "Logged, awaiting triage" },
    { key: "in_progress", label: "In Progress", count: snap.byStatus.inProgress, accent: "#b7791f", tint: "#fffaf0", icon: "▶", desc: "Remediation underway" },
    { key: "on_hold", label: "On Hold", count: snap.byStatus.onHold, accent: "#a16207", tint: "#fefce8", icon: "⏸", desc: "Temporarily paused" },
    { key: "validation", label: "Validation", count: snap.byStatus.validation, accent: "#2b6cb0", tint: "#eff6ff", icon: "✓", desc: "Pending verification" },
    { key: "completed", label: "Completed", count: snap.byStatus.completed, accent: "#15803d", tint: "#f0fdf4", icon: "✔", desc: "Remediation complete" },
    { key: "closed", label: "Remediated", count: snap.byStatus.closed, accent: "#16a34a", tint: "#f0fff4", icon: "●", desc: "Resolved and remediated" },
    { key: "risk_accepted", label: "Risk Accepted", count: snap.byStatus.riskAccepted, accent: "#6b46c1", tint: "#faf5ff", icon: "⚑", desc: "Accepted as residual risk" },
  ];
  const points = trend.points.map((p) => ({
    title: p.title, endDate: p.date, createdAt: p.createdAt || p.date, primary: p.primary,
    passed: p.passed, failed: p.failed, totalStatements: p.totalStatements, totalApplicable: p.totalApplicable,
    scopePercent: p.scopePercent, compliancePercent: p.compliancePercent,
  }));
  return `<script>
window.FINDINGS_SNAPSHOT = ${JSON.stringify({ total: snap.total, severity, status, severityCoverageHint: snap.severityCoverageHint })};
window.FSNAP_ACTIVITY = ${JSON.stringify(snap.activity ?? [])};
window.__TREND_POINTS__ = ${JSON.stringify(points)};
window._embeddedEvidence = ${JSON.stringify(embeddedEvidence ?? {})};
window.__REPORT_AUDIENCE__ = ${JSON.stringify(audience)};
window.__CI_GROUPS__ = ${JSON.stringify(ci?.groups?.map((g) => ({
  key: g.key,
  groupId: g.groupId || g.key,
  title: g.title,
  sspBaselineTotal: g.sspBaselineTotal,
  summary: g.summary,
})) ?? [])};
</script>`;
}

/* previewEvidence() needs window._embeddedEvidence bytes; in the iframe (no auth
   token in the HTML) this override asks the parent to fetch + cache them first. */
const EVIDENCE_BRIDGE = `<script>(function(){
  if (typeof window.previewEvidence !== 'function' || window.parent === window) return;
  var orig = window.previewEvidence, pending = {};
  window.addEventListener('message', function(e){
    var d = (e && e.data) || {};
    if (d.type !== 'rego-evidence-result' || !d.resourceId) return;
    if (d.base64) {
      window._embeddedEvidence = window._embeddedEvidence || {};
      window._embeddedEvidence[d.resourceId] = { fileName: d.fileName, contentType: d.contentType, base64: d.base64 };
    }
    var cb = pending[d.resourceId]; delete pending[d.resourceId];
    if (cb) cb();
  });
  window.previewEvidence = function(){
    var rid = window._currentResourceId;
    var have = rid && window._embeddedEvidence && window._embeddedEvidence[rid];
    if (rid && !have) {
      pending[rid] = function(){ orig.call(window); };
      window.parent.postMessage({ type: 'rego-evidence-request', resourceId: rid }, '*');
      return;
    }
    return orig.call(window);
  };
})();</scr` + `ipt>`;

export type ReportAudience = "auditor" | "system_owner";
export type EmbeddedEvidence = Record<string, { fileName?: string; contentType?: string; base64: string }>;
export interface BuildOptions {
  execSummary?: { state: ExecSummaryState; text?: string }; // default derives from payload.narrative
  embeddedEvidence?: EmbeddedEvidence; // for downloads: base64 evidence so preview works offline
  audience?: ReportAudience;
}

/* Pull evidence resourceIds out of CAR rows' pre-rendered showEvidence(...) HTML, to fetch + embed for offline download. */
export function collectEvidenceResourceIds(p: ReportPayload): string[] {
  const ids = new Set<string>();
  // "resourceId" + the UUID after it, regardless of quote encoding (', ", &#39;, &quot;, ...).
  const re = /resourceId[\s\S]{0,12}?([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})/g;
  for (const r of p.assessment.results) {
    const html = r.dataSourceHtml ?? "";
    let m: RegExpExecArray | null;
    while ((m = re.exec(html))) ids.add(m[1]);
  }
  return [...ids];
}

export function buildAuditReportHtml(p: ReportPayload, opts: BuildOptions = {}): string {
  const exec = opts.execSummary ?? { state: "ready" as ExecSummaryState, text: p.executive.narrative.join("\n\n") };
  const audience = opts.audience ?? "system_owner";
  const assessedControlIds = new Set(
    (p.assessment?.results ?? []).map((r) => r.controlId?.trim().toLowerCase()).filter(Boolean),
  );
  let catalogIdx = 0;
  return getReportTemplate()
    .replace("<body>", `<body data-audience="${audience}">`)
    .replace("</body>", EVIDENCE_BRIDGE + "</body>")
    .replace("<!--METADATA-->", () => metadata(p.meta))
    .replace(/<!--CATALOG_\d+-->/g, () => (catalogIdx++ === 0 ? catalogCard(p.catalog) : profileCard(p.catalog)))
    .replace("<!--EXEC_KPI-->", () => execKpi(p.executive))
    .replace("<!--EXEC_CIA-->", () => execCia(p.executive))
    .replace("<!--EXEC_NARRATIVE-->", () => execNarrative(p.executive, exec))
    .replace("<!--CI_ROWS-->", () => ciRows(p.controlImplementation, assessedControlIds))
    .replace("<!--CAR_ROWS-->", () => carRows(p.assessment, p.meta.sspName, p.catalog))
    .replace("<!--FINDINGS_ROWS-->", () => findingsRows(p.findings))
    .replace("<!--RA_ITEMS-->", () => raItems(p.findings))
    .replace("<!--HISTORY_ROWS-->", () => historyRows(p.history))
    .replace("<!--DATA_ISLANDS-->", () => dataIslands(p.findingsSnapshot, p.trend, p.controlImplementation, opts.embeddedEvidence, audience));
}
